/* eslint-disable lyrahealth/no-react-native-text */
import React from 'react'
import { type IntlShape } from 'react-intl'

import { StyleSheet, Text, View } from '@react-pdf/renderer'
import { CountryCode } from 'libphonenumber-js'
import { capitalize } from 'lodash-es'

import { getClientPreferredNameIfExists } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { lightThemeColors } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/colors'
import { formatPhoneNumberForDisplay } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { getTimeZoneName } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/timeZoneUtils'
import { formatDOB, formatPronouns } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { hasSelfHarmFromLatestOutcome } from '@lyrahealth-inc/shared-app-logic/src/features/outcomes/utils'
import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { getAge } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/utils'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import {
  RISK_BADGE_TEXT,
  SELF_HARM_BADGE_TEXT,
} from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/profileCardBadge/ProfileCardBadge'

export const styles = StyleSheet.create({
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#01798D',
    marginBottom: 20,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#01798D',
  },
  border: {
    borderBottomWidth: 1,
    borderBottomColor: '#DFDBD6',
    marginTop: 16,
    marginBottom: 16,
  },
  profileCard: {
    borderWidth: 1,
    borderColor: '#E5E5E5',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 20,
    marginBottom: 20,
  },
  profileCardName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1A1918',
    marginBottom: 8,
  },
  badgesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
  },
  specialAlertBadge: {
    backgroundColor: lightThemeColors.backgroundWarning,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginRight: 8,
  },
  specialAlertBadgeText: {
    fontSize: 10,
    color: lightThemeColors.textWarning,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  riskBadge: {
    backgroundColor: lightThemeColors.backgroundError,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginRight: 8,
  },
  riskBadgeText: {
    fontSize: 10,
    color: lightThemeColors.textError,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  programNameBadge: {
    backgroundColor: lightThemeColors.cardBackgroundSecondary,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginRight: 8,
  },
  programNameBadgeText: {
    fontSize: 10,
    color: lightThemeColors.primaryButtonPeriwinkleBackgroundPressed,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  profileCardTopInfoText: {
    fontSize: 12,
    color: '#4F4D4A',
    marginBottom: 4,
  },
  profileCardInfoRow: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  profileCardLabel: {
    fontSize: 10,
    color: '#777673',
    fontWeight: 'bold',
    width: 80,
    marginRight: 12,
  },
  profileCardValue: {
    fontSize: 12,
    color: '#333333',
    flex: 1,
  },
})

export const ClientProfileCardPDF: React.FC<{
  clientDetails: ClientObject
  intl: IntlShape
  programName?: string
  isEngageDAProvider?: boolean
}> = ({ clientDetails, intl, programName, isEngageDAProvider }) => {
  const clientPreferredName = getClientPreferredNameIfExists(clientDetails) || clientDetails.full_name
  const clientLegalName = clientDetails.full_name
  const computedAge = getAge({ date: clientDetails.date_of_birth, dateFormat: '' })
  const age = Number.isNaN(computedAge) ? '' : computedAge.toString()
  const phoneNumber = formatPhoneNumberForDisplay(clientDetails.phone, (clientDetails.country as CountryCode) || 'US')
  const dob = formatDOB(clientDetails.date_of_birth)
  const gender = capitalize(clientDetails.gender)
  const ethnicity = clientDetails.race?.join('/')
  const longTimeZone = clientDetails.time_zone_full ? getTimeZoneName({ timeZone: clientDetails.time_zone_full }) : ''
  const shortTimeZone = clientDetails.time_zone ? ` (${clientDetails.time_zone})` : ''
  const displayTimeZone = `${longTimeZone}${shortTimeZone}`
  const lyraId = clientDetails.id
  const formattedPronouns = formatPronouns(clientDetails.pronouns)

  const hasRiskBadge = isEngageDAProvider
    ? clientDetails.self_harm || hasSelfHarmFromLatestOutcome(clientDetails.outcomes)
    : !!clientDetails.self_harm
  const hasSpecialAlertBadge =
    clientDetails?.eligibility_fields?.nuclear_badge_flag === 'yes' ||
    clientDetails?.eligibility_fields?.nuclear_badge_flag === 'true'

  const clientCountry =
    clientDetails?.country && clientDetails.country.toUpperCase() !== 'US'
      ? clientDetails.country.toLocaleUpperCase()
      : undefined
  return (
    <View>
      <Text style={styles.sectionTitle}>Client Profile</Text>

      <View style={styles.profileCard}>
        {(hasRiskBadge || hasSpecialAlertBadge || programName) && (
          <View style={styles.badgesContainer}>
            {hasSpecialAlertBadge && (
              <View style={styles.specialAlertBadge}>
                <Text style={styles.specialAlertBadgeText}>Special Alert</Text>
              </View>
            )}
            {hasRiskBadge && (
              <View style={styles.riskBadge}>
                <Text style={styles.riskBadgeText}>{isEngageDAProvider ? RISK_BADGE_TEXT : SELF_HARM_BADGE_TEXT}</Text>
              </View>
            )}
            {programName && (
              <View style={styles.programNameBadge}>
                <Text style={styles.programNameBadgeText}>
                  {getProgramDisplayLabel(intl, programName, 'clientProfile')}
                </Text>
              </View>
            )}
          </View>
        )}

        <Text style={styles.profileCardName}>{clientPreferredName}</Text>

        {clientLegalName && <Text style={styles.profileCardTopInfoText}>{clientLegalName} (legal)</Text>}
        {formattedPronouns && <Text style={styles.profileCardTopInfoText}>{formattedPronouns}</Text>}
        {age && <Text style={styles.profileCardTopInfoText}>Age {age}</Text>}
        {phoneNumber && <Text style={styles.profileCardTopInfoText}>{phoneNumber}</Text>}

        <View style={styles.border} />

        {dob && (
          <View style={styles.profileCardInfoRow}>
            <Text style={styles.profileCardLabel}>DOB:</Text>
            <Text style={styles.profileCardValue}>{dob}</Text>
          </View>
        )}
        {gender && (
          <View style={styles.profileCardInfoRow}>
            <Text style={styles.profileCardLabel}>Gender:</Text>
            <Text style={styles.profileCardValue}>{gender}</Text>
          </View>
        )}
        {ethnicity && (
          <View style={styles.profileCardInfoRow}>
            <Text style={styles.profileCardLabel}>Ethnicity:</Text>
            <Text style={styles.profileCardValue}>{ethnicity}</Text>
          </View>
        )}
        {displayTimeZone && (
          <View style={styles.profileCardInfoRow}>
            <Text style={styles.profileCardLabel}>Timezone:</Text>
            <Text style={styles.profileCardValue}>{displayTimeZone}</Text>
          </View>
        )}
        {clientCountry && (
          <View style={styles.profileCardInfoRow}>
            <Text style={styles.profileCardLabel}>Country:</Text>
            <Text style={styles.profileCardValue}>{clientCountry}</Text>
          </View>
        )}
        {lyraId && (
          <View style={styles.profileCardInfoRow}>
            <Text style={styles.profileCardLabel}>Lyra ID:</Text>
            <Text style={styles.profileCardValue}>{lyraId}</Text>
          </View>
        )}
      </View>
    </View>
  )
}
